import React, {useEffect, useMemo, useRef, useState} from "react";
import "./BusinessEventManagement.css";
import { supabase } from '../../supabase_client.jsx';

export function getUpcomingEvents(events, today) {
  if (!events) return [];
  return events
    .filter(e => new Date(e.date) >= today && e.is_approved && !e.is_reported)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
}

export function getPendingEvents(events) {
  if (!events) return [];
  return events
    .filter(e => e.is_approved !== true && !e.is_reported)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
}

export function getReportedEvents(events) {
  if (!events) return [];
  return events.filter(e => e.is_reported === true);
}

function generateCheckInCode() {
    const letters = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // omit I/O to avoid confusion
    const digits = "0123456789";
    const l1 = letters[Math.floor(Math.random() * letters.length)];
    const l2 = letters[Math.floor(Math.random() * letters.length)];
    const d1 = digits[Math.floor(Math.random() * digits.length)];
    const d2 = digits[Math.floor(Math.random() * digits.length)];
    return `${l1}${l2}${d1}${d2}`;
}

export default function BusinessEventManagement( { currentUser, onViewSelect, onAddEvent, editEvent: editEventProp, onProfileSetupComplete}) {
    const handleProfileSetupComplete = () => {
        setIsProfileComplete(true);
        if (onProfileSetupComplete) onProfileSetupComplete();
    };
    const businessUserId = currentUser?.user?.id || currentUser?.id;
    
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [editEvent, setEditEvent] = useState(null);
    const [events, setEvents] = useState(null);
    const [formData, setFormData] = useState({});
    const [deleteConfirmation, setDeleteConfirmation] = useState(false);
    const [expandedLists, setExpandedLists] = useState({
        approved: false,
        pending: false,
        past: false
    });

    const [checkInCodes, setCheckInCodes] = useState({});

    // Photo upload state
    const [editPhotoFile, setEditPhotoFile] = useState(null);
    const [editPhotoPreview, setEditPhotoPreview] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const editFileInputRef = useRef(null);

    const today = new Date();

    const [logoUrl, setLogoUrl] = useState("");
    const [isProfileComplete, setIsProfileComplete] = useState(true);

    const [username, setUsername] = useState("");

    const initials = useMemo(() => {
    if (!username) return "B";
    return username.trim().split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
    }, [username]);
    
    // Step 1: resolve username from users table, Step 2: fetch only their events
    useEffect(() => {
        const fetchEvents = async () => {
            if (!businessUserId) {
                    console.log("DEBUG: No businessUserId found in props");
                    return;
                }

            // 1. Get the username for this specific business user
            // Note: Ensure businessUserId matches the 'user_id' (UUID) in table
            const { data: userData, error: userError } = await supabase
                .from("users")
                .select("username")
                .eq("user_id", businessUserId) 
                .single();

            if (userError || !userData?.username) {
                console.log("DEBUG: User resolution failed", userError); 
                setEvents([]);
                return;
            }

            setUsername(userData.username);

            const { data: bizData } = await supabase
                .from("businesses")
                .select("logo_url, name, has_setup_profile")
                .eq("user_id", businessUserId)
                .single();
    
            setLogoUrl(bizData?.logo_url || "");

            if(bizData){
                setIsProfileComplete(bizData.has_setup_profile === true); 
            }

            console.log("Current Username:", userData.username)
            // 2. Fetch events where event_owner matches that username
            const { data, error } = await supabase
                .from("events")
                .select("*")
                .ilike("event_owner", userData.username.trim()); // Matches text column in image_ad9208.png

            if (error) {
                console.error("DEBUG: Supabase fetch error:", error.message);
                setEvents([]);
                return;
            }

            console.log("DEBUG: Raw events from DB:", data);
            setEvents(data || []);
        };

        fetchEvents();
    }, [businessUserId]);

    // Reset photo state when the edit modal opens and closes
    useEffect(() => {
        if (!editEvent) {
            setEditPhotoFile(null);
            setEditPhotoPreview(null);
        }
    }, [editEvent]);

    useEffect(() => {
        if (editEventProp) {
            setEditEvent(editEventProp);
            setFormData({});
        }
        }, [editEventProp]);

    const getApprovedEvents = (events) => {
        // if (!events) {
        //     return
        // }
        // return events.filter(event => event.is_approved && new Date(event.date) >= today);
        if (!events) return;
        const filtered = events
            .filter(event => event.is_approved && new Date(event.date) >= today)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
        console.log("DEBUG: Approved filter result:", filtered); 
        return filtered;
    }

    const getPastEvents = (events) => {
        if (!events) {
            return
        }
        return events
            .filter(event => event.is_approved && new Date(event.date) < today)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    const getPendingEvents = (events) => {
        // if (!events) {
        //     return
        // }
        // return events.filter(event => !event.is_approved && new Date(event.date) >= today);
        if (!events) return;
        const filtered = events
            .filter(event => !event.is_approved && new Date(event.date) >= today)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        console.log("DEBUG: Pending filter result:", filtered);
        return filtered;
    }

    const getCheckInCode = (event) => {
        if (event.checkin_code) return event.checkin_code;
        // Fallback: show "—" if not yet saved (shouldn't happen for approved events)
        return "—";
    };

    const ensureCheckInCode = async (eventId) => {
        const event = events.find(e => e.id === eventId);
        if (event?.checkin_code) return event.checkin_code;
        const code = generateCheckInCode();
        await supabase.from("events").update({ checkin_code: code }).eq("id", eventId);
        setEvents(prev => prev.map(e => e.id === eventId ? { ...e, checkin_code: code } : e));
        return code;
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
        ...prev,
        [field]: value
        }));
    };

    // handle photo file selection in the edit modal
    const handleEditPhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setEditPhotoFile(file);
        setEditPhotoPreview(URL.createObjectURL(file));
    };

    // upload the new photo and return its public URL (null on failure)
    const uploadEditPhoto = async () => {
        if (!editPhotoFile) return null;

        const fileExt = editPhotoFile.name.split(".").pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).slice(2)}.${fileExt}`;
        const filePath = `event-photos/${fileName}`;

        const { error: uploadError } = await supabase.storage
            .from("events")
            .upload(filePath, editPhotoFile, { upsert: false });
        
        if (uploadError) {
            alert("Photo upload failed: " + uploadError.message);
            return null;
        }

        const { data: urlData } = supabase.storage
            .from("events")
            .getPublicUrl(filePath);

        return urlData?.publicUrl ?? null;
    };

    const handleSave = async () => {
        try {
            setPhotoUploading(true);
            let updatedData = { ...formData };

            if (editPhotoFile) {
                const newUrl = await uploadEditPhoto();
                if (newUrl) updatedData.image_url = newUrl;
            }

            const { error } = await supabase
            .from("events")
            .update(updatedData)
            .eq("id", editEvent.id);

            if (error) { alert("Save failed: " + error.message); return; }

            setEvents(prev =>
            prev.map(e => e.id === editEvent.id ? { ...e, ...updatedData } : e)
            );
            setEditEvent(null);
            if (updatedData.is_approved !== false && (editEvent.is_approved || updatedData.is_approved)) {
                await ensureCheckInCode(editEvent.id);
            }
            setFormData({});
        } catch (err) {
            console.error(err);
        } finally {
            setPhotoUploading(false);
        }
    };

    const handleSaveAndResubmit = async () => {
        try {
            setPhotoUploading(true);
            let updatedData = { ...formData, is_approved: false, is_reported: false };

            if (editPhotoFile) {
                const newUrl = await uploadEditPhoto();
                if (newUrl) updatedData.image_url = newUrl;
            }

            const { error } = await supabase
                .from("events")
                .update(updatedData)
                .eq("id", editEvent.id);

            if (error) { alert("Save failed: " + error.message); return; }

            setEvents(prev =>
                prev.map(e => e.id === editEvent.id ? { ...e, ...updatedData } : e)
            );
            setEditEvent(null);
            setFormData({});
        } catch (err) {
            console.error(err);
        } finally {
            setPhotoUploading(false);
        }
    };

    const handleDelete = async () => {
        try {
            const { error } = await supabase
                .from("events")
                .delete()
                .eq("id", editEvent.id);

            if (error) { alert("Delete failed: " + error.message); return; }

            setEvents(prev => prev.filter(e => e.id !== editEvent.id));
            setEditEvent(null);
            setDeleteConfirmation(false);
            setFormData({});
        } catch (err) {
            console.error(err);
        }
    };

    const renderEventList = (events, listType) => {
        if (!events) {
            return
        }

        const isExpanded = expandedLists[listType];
        const visibleEvents = isExpanded ? events : events.slice(0, 5);
        const extraCount = events.length - 5;

        return (
            <div className="event-list-wrapper">
                {visibleEvents.map(event => (
                    <div key={event.id} className="event-item" onClick={async () => {
                        setSelectedEvent(event);
                        if (event.is_approved) {
                            const code = await ensureCheckInCode(event.id);
                            setSelectedEvent(prev => prev?.id === event.id ? { ...prev, checkin_code: code } : prev);
                        }
                    }}>
                    <div className="event-text">
                        <span className="event-name">{event.name}</span>
                        <span className="event-date">{event.date}</span>
                    </div>

                    <span
                        className="edit-icon"
                        onClick={(e) => {
                            e.stopPropagation();
                            setEditEvent(event);
                    }}
                    >
                    ✎
                    </span>
                    </div>
                ))}

                {!isExpanded && extraCount > 0 && (
                    <div
                    className="expand-toggle"
                    onClick={() => setExpandedLists(prev => ({...prev, [listType]: true}))} 
                    >
                    + {extraCount} more events
                    </div>
                )}

                {isExpanded && extraCount > 0 && (
                    <div
                    className="collapse-toggle"
                    onClick={() => setExpandedLists(prev => ({...prev, [listType]: false}))}
                    >
                    Collapse
                    </div>
                )}
            </div>
        );
    };

    // current photo to show in the edit modal: new selection > existing event photo > placeholder
    const editCurrentPhoto = editPhotoPreview || editEvent?.image_url;

    return (
        <div className="event-management-container">
            <div className="home-card">

                <div className="app-title-banner">
                    <h1 className="app-title-text">Do You Wanna Go?</h1>
                </div>
            {/* header */}
            <div className="calendar-top-nav">
                <div className="nav-left">
                    <button className="nav-tab" onClick={() => onViewSelect('businessHome')}>
                        General
                    </button>
                    <button className="nav-tab" onClick={() => onViewSelect('business')}>
                        Calendar
                    </button>
                    <button className="nav-tab" onClick={() => onViewSelect('businessCRMDash')}>Dashboard</button>
                    <button className="nav-tab active">Event Management</button>
                </div>
                <div className="nav-right">
                    <div className="nav-avatar-circle" style={{ position: "relative" }} onClick={() => onViewSelect("business-profile")}>
                    {logoUrl
                        ? <img src={logoUrl} alt="profile" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
                        : initials
                    }
                    {!isProfileComplete && <span className="avatar-dot" />}
                    </div>
                </div>
            </div>

            <div className="add-event-container">
                <button className="add-event-button" onClick={() => onAddEvent()}>+ Add Event</button>
            </div>
            <div className="event-blocks-container">

                {/* approved events */}
                <div className="approved-events event-block">
                    <h3>Approved Events</h3>
                    {renderEventList(getApprovedEvents(events), 'approved')}
                </div>

                <div className="right-column">
                    {/* pending events */}
                    <div className="pending-events event-block">
                        <h3>Pending Events</h3>
                        {renderEventList(getPendingEvents(events), 'pending')}
                    </div>

                    {/* past events */}
                    <div className="past-events event-block">
                        <h3>Past Events</h3>
                        {renderEventList(getPastEvents(events), 'past')}
                    </div>
                </div>

                {/*event details popup */}
                {selectedEvent && (
                    <div className="popup-overlay" onClick={() => setSelectedEvent(null)}>
                    <div className="popup" onClick={(e) => e.stopPropagation()}>
                        <button className="close-btn" onClick={() => setSelectedEvent(null)}>✕</button>

                        {selectedEvent.image_url ? (
                            <img src={selectedEvent.image_url} alt={selectedEvent.name} className="popup-image" />
                        ) : (
                            <div className="detail-image-placeholder">No Image</div>
                        )}

                        <div className="popup-header" style={{ marginTop: 16 }}>
                        <div>
                            <h2 className="popup-title">{selectedEvent.name}</h2>
                            <span className={`popup-badge ${selectedEvent.is_approved ? 'badge-approved' : 'badge-pending'}`}>
                            {selectedEvent.is_approved ? 'Approved' : 'Pending'}
                            </span>
                        </div>
                        </div>

                        <div className="popup-divider" />

                        <div className="popup-details">
                        {selectedEvent.date && (
                            <div className="popup-row">
                            <span className="popup-label">Date</span>
                            <span className="popup-value">{selectedEvent.date}</span>
                            </div>
                        )}
                        {(selectedEvent.start_time || selectedEvent.end_time) && (
                            <div className="popup-row">
                            <span className="popup-label">Time</span>
                            <span className="popup-value">
                                {selectedEvent.start_time}{selectedEvent.end_time ? ` – ${selectedEvent.end_time}` : ''}
                            </span>
                            </div>
                        )}
                        {selectedEvent.location && (
                            <div className="popup-row">
                            <span className="popup-label">Location</span>
                            <span className="popup-value">{selectedEvent.location}</span>
                            </div>
                        )}
                        {selectedEvent.event_owner && (
                            <div className="popup-row">
                            <span className="popup-label">Event Owner</span>
                            <span className="popup-value">{selectedEvent.event_owner}</span>
                            </div>
                        )}
                        {selectedEvent.description && (
                            <div className="popup-row popup-row-col">
                            <span className="popup-label">Description</span>
                            <span className="popup-value" style={{ textAlign: 'left' }}>{selectedEvent.description}</span>
                            </div>
                        )}
                        {selectedEvent.is_approved && new Date(selectedEvent.date) >= today && (
                            <div className="popup-row popup-row-col">
                            <span className="popup-label">Check-In Code</span>
                            <div className="checkin-code-display">
                                <div className="checkin-code-boxes-readonly">
                                {(selectedEvent.checkin_code || "----").split("").map((char, i) => (
                                    <div key={i} className="checkin-code-box-readonly">
                                    {char}
                                    </div>
                                ))}
                                </div>
                                <p className="checkin-code-hint">Share this code with attendees at your event</p>
                            </div>
                            </div>
                        )}
                        </div>
                    </div>
                    </div>
                )}
                
                {/** edit event popup */}
                {editEvent && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">

                    {/* Close button */}
                    <button
                        className="admin-close-btn"
                        onClick={() => setEditEvent(null)}
                    >
                        ✕
                    </button>

                    {/* Header */}
                    <div className="admin-modal-header">
                        <h2>{editEvent.name}</h2>
                        <span className={`popup-badge ${
                        editEvent.is_approved ? 'badge-approved' :
                        editEvent.is_reported ? 'badge-reported' :
                        'badge-pending'
                        }`}>
                        {editEvent.is_approved ? 'Approved' : editEvent.is_reported ? 'Reported' : 'Pending'}
                        </span>
                    </div>

                    {/* Body */}
                    <div className="admin-modal-body">

                        {/* Left column */}
                        <div className="admin-left">
                            {/** photo upload box */}
                            <input
                                ref={editFileInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleEditPhotoChange}
                            /> 
                            <div
                                className="admin-image-box edit-image-upload-box"
                                onClick={() => editFileInputRef.current?.click()}
                                style={{ cursor: "pointer", position: "relative", overflow: "hidden" }}
                            >
                                {editCurrentPhoto ? (
                                    <>
                                        <img src={editCurrentPhoto} alt={editEvent.name} />
                                        <div className="edit-image-overlay">
                                            <span>Change Photo</span>
                                        </div>
                                    </>
                                ) : (
                                    <div className="admin-image-placeholder edit-image-placeholder-upload">
                                        <div style={{ fontSize: 24, marginBottom: 4 }}>📷</div>
                                        <span>Click to upload photo</span>
                                    </div>
                                )}
                            </div>

                        <div className="admin-meta-grid">
                            <div className="admin-field">
                            <label>Date</label>
                            <input
                                type="date"
                                value={formData.date ?? editEvent.date ?? ''}
                                onChange={(e) => handleChange("date", e.target.value)}
                            />
                            </div>

                            <div className="admin-field">
                            <label>Start Time</label>
                            <input
                                type="time"
                                value={formData.start_time ?? editEvent.start_time ?? ''}
                                onChange={(e) => handleChange("start_time", e.target.value)}
                            />
                            </div>

                            <div className="admin-field">
                            <label>End Time</label>
                            <input
                                type="time"
                                value={formData.end_time ?? editEvent.end_time ?? ''}
                                onChange={(e) => handleChange("end_time", e.target.value)}
                            />
                            </div>

                            <div className="admin-field">
                            <label>Location</label>
                            <input
                                type="text"
                                value={formData.location ?? editEvent.location ?? ''}
                                onChange={(e) => handleChange("location", e.target.value)}
                            />
                            </div>
                        </div>
                        </div>

                        {/* Right column */}
                        <div className="admin-right">
                        <div className="admin-field">
                            <label>Event Owner</label>
                            <input
                                type="text"
                                value={formData.event_owner ?? editEvent.event_owner ?? ''}
                                onChange={(e) => handleChange("event_owner", e.target.value)}
                            />
                        </div>
                        <div className="admin-field">
                            <label>Description</label>
                            <textarea
                            value={formData.description ?? editEvent.description ?? ''}
                            onChange={(e) => handleChange("description", e.target.value)}
                            />
                        </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="admin-modal-footer">
                        {deleteConfirmation ? (
                            <>
                                <span style={{ fontSize: 13, color: '#666', alignSelf: 'center' }}>
                                    Are you sure?
                                </span>
                                <button className="admin-delete-btn" onClick={handleDelete}>
                                    Yes, Delete
                                </button>
                                <button className="admin-save-btn" onClick={() => setDeleteConfirmation(false)}>
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <>
                                {editEvent.is_approved ? (
                                    <button
                                        className="admin-save-btn"
                                        onClick={handleSaveAndResubmit}
                                        disabled={photoUploading}
                                    >
                                        {photoUploading ? "Saving…" : "Save & Resubmit for Approval"}
                                    </button>
                                ) : (
                                    <button
                                        className="admin-save-btn"
                                        onClick={editEvent.is_reported ? handleSaveAndResubmit : handleSave}
                                        disabled={photoUploading}
                                    >
                                        {photoUploading
                                            ? "Saving…"
                                            : editEvent.is_reported
                                                ? "Save & Resubmit for Approval"
                                                : "Save"
                                        }
                                    </button>
                                )}
                                <button
                                    className="admin-delete-btn"
                                    onClick={() => setDeleteConfirmation(true)}
                                >
                                    Delete
                                </button>
                            </>
                        )}
                    </div>


                    </div>
                </div>
                )}
            </div>
            </div>
        </div>
    );

}